import type { CanDoItem, CommunicationTopicMeta } from "../modules/tutor/api/tutor.client";
import type { SkillProgressEntry } from "../services/api/canDo";

/** Mean rubric ≥ this counts as mastered in progress UIs. */
export const COMM_CAN_DO_MASTER_THRESHOLD = 0.75;

export type CommCanDoBubbleStatus = "mastered" | "in_progress" | "locked";

export type CommCanDoBubbleModel = {
  can_do_id: string;
  statement: string;
  fluency: number;
  accuracy: number;
  appropriateness: number;
  status: CommCanDoBubbleStatus;
  overallPct: number;
};

export type CommTopicProgressGroup = {
  topicId: string;
  topicTitle: string;
  topicHint?: string;
  bubbles: CommCanDoBubbleModel[];
};

export function meanCommSkillPerformance(p: SkillProgressEntry["avg_performance"]): number {
  return (p.fluency + p.accuracy + p.appropriateness) / 3;
}

export function overallPctFromCommSkill(p: SkillProgressEntry["avg_performance"]): number {
  return Math.round(meanCommSkillPerformance(p) * 100);
}

/** Merge teacher curriculum with student communication-log averages (all can-dos per topic). */
export function groupCommunicationCurriculumWithProgress(
  communicationTopics: CommunicationTopicMeta[],
  canDos: CanDoItem[],
  skills: SkillProgressEntry[]
): CommTopicProgressGroup[] {
  const skillMap = new Map(skills.map((s) => [s.can_do_id, s]));
  const byTopic = new Map<string, CanDoItem[]>();
  for (const cd of canDos) {
    const tid = cd.communication_topic_id || "_ungrouped";
    if (!byTopic.has(tid)) byTopic.set(tid, []);
    byTopic.get(tid)!.push(cd);
  }

  const rowForCanDo = (cd: CanDoItem): CommCanDoBubbleModel => {
    const skill = skillMap.get(cd.id);
    const statement =
      (skill?.statement || "").trim() ||
      (cd.statement_en || "").trim() ||
      (cd.statement_ta || "").trim() ||
      "Can-Do goal";
    if (!skill) {
      return {
        can_do_id: cd.id,
        statement,
        fluency: 0,
        accuracy: 0,
        appropriateness: 0,
        status: "locked",
        overallPct: 0,
      };
    }
    const mean = meanCommSkillPerformance(skill.avg_performance);
    return {
      can_do_id: cd.id,
      statement,
      fluency: skill.avg_performance.fluency,
      accuracy: skill.avg_performance.accuracy,
      appropriateness: skill.avg_performance.appropriateness,
      status: mean >= COMM_CAN_DO_MASTER_THRESHOLD ? "mastered" : "in_progress",
      overallPct: overallPctFromCommSkill(skill.avg_performance),
    };
  };

  const groups: CommTopicProgressGroup[] = [];

  for (const t of communicationTopics) {
    const group = byTopic.get(t.id);
    if (!group?.length) continue;
    groups.push({
      topicId: t.id,
      topicTitle: (t.title_en || "").trim() || "Topic",
      topicHint: (t.title_ta || "").trim() || undefined,
      bubbles: group.map(rowForCanDo),
    });
  }

  const ungrouped = byTopic.get("_ungrouped") || [];
  for (const cd of ungrouped) {
    groups.push({
      topicId: cd.id,
      topicTitle:
        (cd.statement_en || "").trim().replace(/^I can\s+/i, "").replace(/\.$/, "") || "Practice goal",
      topicHint: (cd.statement_ta || "").trim() || undefined,
      bubbles: [rowForCanDo(cd)],
    });
  }

  return groups;
}

export function fallbackCommTopicFromSkills(skills: SkillProgressEntry[]): CommTopicProgressGroup {
  return {
    topicId: "_all",
    topicTitle: "Communication practice",
    topicHint: "Your logged Can-Do averages",
    bubbles: skills.map((s) => {
      const mean = meanCommSkillPerformance(s.avg_performance);
      return {
        can_do_id: s.can_do_id,
        statement: (s.statement || "").trim() || "Can-Do goal",
        fluency: s.avg_performance.fluency,
        accuracy: s.avg_performance.accuracy,
        appropriateness: s.avg_performance.appropriateness,
        status: mean >= COMM_CAN_DO_MASTER_THRESHOLD ? "mastered" : "in_progress",
        overallPct: overallPctFromCommSkill(s.avg_performance),
      };
    }),
  };
}
